import { OpPatch } from "json-patch";
import {AxiosResponse} from "axios";
import {useQuery} from "@tanstack/react-query";

import { Axios } from "../../Axios";
import { TypeAluno } from "../../../Models/Domain/Pessoa/Aluno";
import { TypePessoaObfuscada } from "../../../Models/Pessoa/Pessoa";
import { TypeResponsavel } from "../../../Models/Domain/Pessoa/Responsavel";
import { TypeViagem } from "../../../Models/Domain/Viagem/Viagem";

import {BasicQueryType, QueryBasicaParam} from "../../../Utils/RequisicaoUtilitario";
import {TypeInscricaoDoAlunoNaViagem} from "../../../Models/Domain/Viagem/InscricaoDoAlunoNaViagem";
import { queryClient } from "../../../AreaRestritta/AppAreaRestrita";

export interface FiltroDeListagem {
    codigo?: number,
    codigoDaPessoa?: number,
    codigoDoUsuarioDaAreaRestrita?: number,
    cpf?: string,
    passaporte?: string,
    rne?: string,
    nome?: string,
    tipoDeDocumento?: TypeAluno["tipoDeDocumento"],
    documento?: string,
    genero?: TypeAluno["genero"],
    ativo?: boolean,

    inclusoNasViagens?: TypeViagem["codigo"][],
    idadeMaiorOuIgual?: number,
    idadeMenorQue?: number,
}

export class AlunoService {
    public listar(filtro?: FiltroDeListagem ) {
        return Axios.post<TypeAluno[]>(`quiron/pessoa/aluno/listar`, filtro);
    }

    public inserir(payload: TypeAluno) {
        return Axios.post<TypeAluno>(`quiron/pessoa/aluno`, payload);
    }

    public async atualizar(payload: TypeAluno) {
        const response = await Axios.put<void>(`quiron/pessoa/aluno`, payload);

        queryClient.invalidateQueries({ queryKey : ["Pessoa.AlunoResponsavel.Responsavel.ObterAlunosResponsavel"] });

        return response;
    }

    public patch(codigoDoAluno: number, payload: OpPatch[]) {
        return Axios.patch<void>(`quiron/pessoa/aluno/${codigoDoAluno}/`, payload);
    }

    public ativar(codigoDoAluno: number) {
        return Axios.patch<void>(`quiron/pessoa/aluno/${codigoDoAluno}/ativar`);
    }

    public desativar(codigoDoAluno: number) {
        return Axios.patch<void>(`quiron/pessoa/aluno/${codigoDoAluno}/desativar`);
    }

    public static vincularResponsaveisPeloCPF(codigoDoAluno: number, cpfs: string[]) {
        return Axios.post<void>(`quiron/pessoa/aluno/${codigoDoAluno}/responsaveis/`, cpfs);
    }

    public static listarResponsaveis<BuscandoComo extends "responsavel" | "administrador" = "administrador">(codigoDoAluno: number) {
        type Retorno = BuscandoComo extends "administrador" ? TypeResponsavel[] : TypePessoaObfuscada[];
        return Axios.get<Retorno>(`quiron/pessoa/aluno/${codigoDoAluno}/responsaveis/`);
    }

}

export const useListarInscricoes = ({codigoDoAluno, ...props}: BasicQueryType<AxiosResponse<TypeInscricaoDoAlunoNaViagem[]>, {codigoDoAluno: number}>) => (
    useQuery({
        ...props,
        queryKey: ["Domain.Pessoa.AlunoService.listarInscricoes", codigoDoAluno],
        queryFn: () => {
            return Axios.get<TypeInscricaoDoAlunoNaViagem[]>(`quiron/pessoa/aluno/${codigoDoAluno}/inscricoes`);
        },
        staleTime: 1000 * 10
    })
);

export function useAlunoListar({
    ...rest
}:QueryBasicaParam<AxiosResponse<TypeAluno[]>, AxiosResponse<TypeAluno[]>>) {
    return useQuery({
        ...rest,
        queryKey: [`listarAlunosDisponiveis`],
        queryFn: () => new AlunoService().listar({ativo: true})
    });
} 

export const AlunoServiceH = {
    useListarInscricoes
}
